import React, { useMemo, useRef, useState, useEffect } from "react";
import NodeCircle from "./NodeCircle";
import { CustomIcon } from "../shared/CustomIcon";

// =============================================================
// PaginatedTreeView — 3-level window: focus node at top, its
// direct children in middle, its grandchildren in bottom.
// SVG connectors are drawn from measured DOM positions. A row of
// faded dashed placeholders below indicates "Delta and beyond".
// =============================================================

// Synthesises a placeholder vacant node so the 3-Beta / 9-Gamma
// grid stays visually intact even when the tree isn't full yet.
function padChildren(existing = [], parent, count = 3, levelOffset = 1) {
  const arr = [...existing];
  const missing = count - arr.length;
  for (let i = 0; i < missing; i++) {
    arr.push({
      nodeId: `__ph_${parent?.nodeId || "root"}_${levelOffset}_${i}`,
      slotRef: null,
      level: (parent?.level ?? -1) + levelOffset,
      levelName: "",
      status: "Vacant",
      memberId: null,
      memberNumber: null,
      childNodeIds: [],
      _placeholder: true,
    });
  }
  return arr;
}

export default function PaginatedTreeView({
  tree, focusNodeId, members, currentUser, highlightUserNode, healthMap = {},
  onFocusNode, onOpenNode, onBackToTop,
}) {
  const nodes = tree?.nodes || {};
  const focus = focusNodeId ? nodes[focusNodeId] : (tree?.godNodeId ? nodes[tree.godNodeId] : (tree?.alphaNodeId ? nodes[tree.alphaNodeId] : null));
  const parentNode = focus?.parentNodeId ? nodes[focus.parentNodeId] : null;

  // Compute the 1-3-9 grid centered on focus.
  const focusChildren = useMemo(() => {
    if (!focus) return [];
    const kids = (focus.childNodeIds || []).map(id => nodes[id]).filter(Boolean);
    return padChildren(kids, focus, 3, 1);
  }, [focus, nodes]);
  const focusGrandchildren = useMemo(() => {
    const out = [];
    focusChildren.forEach(b => {
      const kids = b._placeholder ? [] : (b.childNodeIds || []).map(id => nodes[id]).filter(Boolean);
      const padded = padChildren(kids, b, 3, 1);
      out.push({ parent: b, children: padded });
    });
    return out;
  }, [focusChildren, nodes]);

  // Breadcrumb — the ancestor chain up to Alpha.
  const breadcrumb = useMemo(() => {
    const path = [];
    let cur = focus;
    while (cur) {
      path.unshift(cur);
      cur = cur.parentNodeId ? nodes[cur.parentNodeId] : null;
    }
    return path;
  }, [focus, nodes]);

  // Level summary counts across the current 3-level window.
  const summary = useMemo(() => {
    const occ = (list) => list.filter(n => !n._placeholder && n.memberId).length;
    const topName   = focus?.levelName || "—";
    const midName   = focusChildren[0]?.levelName || "—";
    const gramName  = focusGrandchildren[0]?.children?.[0]?.levelName || "—";
    return {
      top: { name: topName, count: focus && !focus._placeholder && focus.memberId ? 1 : 0 },
      mid: { name: midName, count: occ(focusChildren) },
      bot: { name: gramName, count: occ(focusGrandchildren.flatMap(g => g.children)) },
    };
  }, [focus, focusChildren, focusGrandchildren]);
  const totalShown = summary.top.count + summary.mid.count + summary.bot.count;

  // Sizes
  const SIZE_TOP = 80, SIZE_MID = 60, SIZE_BOT = 44, SIZE_PLACE = 28;

  const containerRef = useRef(null);
  const [connectors, setConnectors] = useState([]);
  const nodeRefs = useRef({});

  // Recompute SVG connector paths after layout (and on resize).
  useEffect(() => {
    const compute = () => {
      const box = containerRef.current?.getBoundingClientRect();
      if (!box) return;
      const rect = (id) => {
        const el = nodeRefs.current[id];
        if (!el) return null;
        const r = el.getBoundingClientRect();
        return { cx: r.left - box.left + r.width / 2, top: r.top - box.top, bot: r.bottom - box.top };
      };
      const lines = [];
      // Alpha → Beta
      const topId = "top";
      const topR = rect(topId);
      if (topR) {
        focusChildren.forEach((b, i) => {
          const bR = rect(`mid-${i}`);
          if (bR) lines.push({ from: [topR.cx, topR.bot + 4], to: [bR.cx, bR.top - 4] });
        });
      }
      // Beta → Gamma
      focusGrandchildren.forEach((g, gi) => {
        const bR = rect(`mid-${gi}`);
        if (!bR) return;
        g.children.forEach((c, ci) => {
          const cR = rect(`bot-${gi}-${ci}`);
          if (cR) lines.push({ from: [bR.cx, bR.bot + 4], to: [cR.cx, cR.top - 4] });
        });
      });
      setConnectors(lines);
    };
    // Two RAFs to let layout settle
    const raf = requestAnimationFrame(() => requestAnimationFrame(compute));
    window.addEventListener("resize", compute);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", compute); };
  }, [focusChildren, focusGrandchildren, focusNodeId]);

  const setRef = (key) => (el) => { if (el) nodeRefs.current[key] = el; };

  return (
    <div data-testid="paginated-tree" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Breadcrumb + controls */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div data-testid="ptn-breadcrumb" style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", fontSize: 12, fontFamily: "'Montserrat',sans-serif" }}>
          {breadcrumb.map((n, i) => (
            <React.Fragment key={n.nodeId}>
              <button
                type="button"
                data-testid={`ptn-crumb-${n.slotRef}`}
                onClick={() => onFocusNode && onFocusNode(n.nodeId)}
                style={{ background: "none", border: "none", cursor: "pointer", color: i === breadcrumb.length - 1 ? "var(--kc-accent)" : "var(--kc-sub)", fontFamily: "inherit", fontSize: 12, padding: "2px 4px", textDecoration: i === breadcrumb.length - 1 ? "none" : "underline dashed rgba(255,255,255,0.25)" }}
              >{n.levelName}</button>
              {i < breadcrumb.length - 1 && <span style={{ color: "var(--kc-meta)" }}>→</span>}
            </React.Fragment>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {focus && focus.nodeId !== (tree?.godNodeId || tree?.alphaNodeId) && (
            <button className="btn btn-sm btn-outline" data-testid="ptn-back-top" onClick={onBackToTop}>↑ Top</button>
          )}
        </div>
      </div>

      {/* Tree canvas */}
      <div ref={containerRef} style={{ position: "relative", background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 12, padding: "24px 16px", overflowX: "auto" }}>
        {/* SVG connector overlay */}
        <svg
          style={{ position: "absolute", inset: 0, pointerEvents: "none", width: "100%", height: "100%" }}
          preserveAspectRatio="none"
          data-testid="ptn-svg"
        >
          {connectors.map((c, i) => {
            const midY = (c.from[1] + c.to[1]) / 2;
            const d = `M ${c.from[0]} ${c.from[1]} L ${c.from[0]} ${midY} L ${c.to[0]} ${midY} L ${c.to[0]} ${c.to[1]}`;
            return <path key={i} d={d} stroke="var(--kc-accent)" strokeWidth={1.5} fill="none" strokeOpacity={0.75} />;
          })}
        </svg>

        <div style={{ minWidth: 640, display: "flex", flexDirection: "column", gap: 40, position: "relative" }}>
          {/* Alpha (top focus) row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12 }}>
            <div style={{ flex: 1 }} />
            <div ref={setRef("top")} onClick={() => focus && onOpenNode(focus)} style={{ cursor: focus ? "pointer" : "default" }}>
              <NodeCircle
                node={focus}
                members={members}
                size={SIZE_TOP}
                healthOverride={focus && healthMap[focus.nodeId]?.health}
                highlighted={highlightUserNode && currentUser?.memberId && focus?.memberId === currentUser.memberId}
                onClick={() => focus && onOpenNode(focus)}
              />
            </div>
            <div style={{ flex: 1, textAlign: "right" }}>
              <span data-testid="ptn-badge-top" style={{ display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "4px 10px", borderRadius: 999, color: "var(--kc-accent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)", background: "color-mix(in srgb, var(--kc-accent) 6%, transparent)", fontFamily: "'Montserrat',sans-serif" }}>
                {summary.top.name} · {summary.top.count} {summary.top.count === 1 ? "Person" : "People"}
              </span>
            </div>
          </div>

          {/* Beta (middle) row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12 }}>
            <div style={{ flex: 1 }} />
            <div style={{ display: "flex", justifyContent: "space-between", flex: 4, gap: 8 }}>
              {focusChildren.map((b, i) => (
                <div key={b.nodeId} ref={setRef(`mid-${i}`)} style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                  <NodeCircle
                    node={b}
                    members={members}
                    size={SIZE_MID}
                    healthOverride={healthMap[b.nodeId]?.health}
                    highlighted={highlightUserNode && currentUser?.memberId && b.memberId === currentUser.memberId}
                    onClick={() => !b._placeholder && onOpenNode(b)}
                  />
                </div>
              ))}
            </div>
            <div style={{ flex: 1, textAlign: "right" }}>
              <span data-testid="ptn-badge-mid" style={{ display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "4px 10px", borderRadius: 999, color: "var(--kc-accent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)", background: "color-mix(in srgb, var(--kc-accent) 6%, transparent)", fontFamily: "'Montserrat',sans-serif" }}>
                {summary.mid.name} · {summary.mid.count} {summary.mid.count === 1 ? "Person" : "People"}
              </span>
            </div>
          </div>

          {/* Gamma (bottom) row — grouped in sets of 3 under each Beta */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12 }}>
            <div style={{ flex: 1 }} />
            <div style={{ display: "flex", justifyContent: "space-between", flex: 4, gap: 24 }}>
              {focusGrandchildren.map((g, gi) => (
                <div key={g.parent.nodeId + "-group"} style={{ flex: 1, display: "flex", justifyContent: "space-between", gap: 6 }}>
                  {g.children.map((c, ci) => (
                    <div key={c.nodeId} ref={setRef(`bot-${gi}-${ci}`)} style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                      <NodeCircle
                        node={c}
                        members={members}
                        size={SIZE_BOT}
                        healthOverride={healthMap[c.nodeId]?.health}
                        highlighted={highlightUserNode && currentUser?.memberId && c.memberId === currentUser.memberId}
                        onClick={() => !c._placeholder && onOpenNode(c)}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div style={{ flex: 1, textAlign: "right" }}>
              <span data-testid="ptn-badge-bot" style={{ display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "4px 10px", borderRadius: 999, color: "var(--kc-accent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)", background: "color-mix(in srgb, var(--kc-accent) 6%, transparent)", fontFamily: "'Montserrat',sans-serif" }}>
                {summary.bot.name} · {summary.bot.count} {summary.bot.count === 1 ? "Person" : "People"}
              </span>
            </div>
          </div>

          {/* Faded placeholders row */}
          <div data-testid="ptn-fade-row" style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center", marginTop: 6, opacity: 0.55 }}>
            <div style={{ display: "flex", gap: 6, justifyContent: "center", opacity: 0.2 }}>
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i} style={{ width: SIZE_PLACE, height: SIZE_PLACE, borderRadius: "50%", border: "1px dashed var(--kc-accent)" }} />
              ))}
            </div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontStyle: "italic", fontSize: 11, color: "color-mix(in srgb, var(--kc-accent) 65%, transparent)" }}>
              Tree continues — Delta and beyond
            </div>
          </div>
        </div>
      </div>

      {/* Totals + View Deeper */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div data-testid="ptn-total" style={{ fontSize: 11, color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif" }}>
          Showing <strong>{totalShown}</strong> of <strong>{Object.values(nodes).filter(n => n.memberId).length}</strong> total members
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {/* View Deeper: focus the first Beta that has children */}
          <button
            className="btn btn-sm btn-outline"
            data-testid="ptn-view-deeper"
            onClick={() => {
              const target = focusChildren.find(b => !b._placeholder && (b.childNodeIds || []).length > 0);
              if (target && onFocusNode) onFocusNode(target.nodeId);
            }}
            disabled={!focusChildren.some(b => !b._placeholder && (b.childNodeIds || []).length > 0)}
          >
            View Deeper <CustomIcon name="arrow-right" size={10} />
          </button>
        </div>
      </div>
    </div>
  );
}
